import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/admin/login")({
  head: () => ({
    meta: [
      { title: "Admin bejelentkezés — LAKROSZ" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminLogin,
});

function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) navigate({ to: "/admin/ertekelesek" });
    });
  }, [navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setLoading(true);
    try {
      if (mode === "signup") {
        const { error: err } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin + "/admin/login" },
        });
        if (err) throw err;
        setInfo(
          "Fiók létrehozva. Most jelentkezz be. (Admin jogosultság megadásához az adatbázisban add hozzá a user_roles táblához a 'admin' szerepkört.)",
        );
        setMode("signin");
      } else {
        const { error: err } = await supabase.auth.signInWithPassword({ email, password });
        if (err) throw err;
        navigate({ to: "/admin/ertekelesek" });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ismeretlen hiba");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-16">
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center font-display text-2xl tracking-wider mb-8">
          LAKROSZ<span className="text-primary">.</span> AUTÓSZERVIZ
        </Link>
        <div className="bg-surface border border-border rounded-2xl p-8">
          <h1 className="font-display text-3xl mb-1">Admin {mode === "signup" ? "regisztráció" : "bejelentkezés"}</h1>
          <p className="text-sm text-muted-foreground mb-6">
            Csak munkatársak számára.
          </p>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full bg-background border border-border rounded-xl px-4 py-3 outline-none focus:border-primary"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-muted-foreground">Jelszó</label>
              <input
                type="password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full bg-background border border-border rounded-xl px-4 py-3 outline-none focus:border-primary"
              />
            </div>
            {error && <div className="text-sm text-destructive">{error}</div>}
            {info && <div className="text-sm text-primary">{info}</div>}
            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-full bg-primary text-primary-foreground font-semibold disabled:opacity-50"
            >
              {loading ? "Folyamatban..." : mode === "signup" ? "Regisztráció" : "Belépés"}
            </button>
          </form>
          <button
            type="button"
            onClick={() => {
              setMode(mode === "signin" ? "signup" : "signin");
              setError(null);
              setInfo(null);
            }}
            className="mt-4 w-full text-sm text-muted-foreground hover:text-primary"
          >
            {mode === "signin" ? "Még nincs fiókod? Regisztrálj" : "Van már fiókod? Jelentkezz be"}
          </button>
        </div>
      </div>
    </div>
  );
}
